export interface GeminiExperienceData {
  name: string;
  city: string;
  description: string;
  nomeChatbot: string;
  frase_avvio_percorso?: string;
  messIntermedio: string;
  messFinale: string;
  tutorLanguageRegister?: string;
  tutorEpoch?: string;
  tutorVoiceCode?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface GeminiGeniusLociData {
  name: string;
  bio: string;
  specialties: string[];
  quote: string;
  personality: string;
  welcomeMessage?: string;
  languageRegister?: string;
  historicalEpoch?: string;
  voiceCode?: string;
}

export interface ExperienceAutofillOptions {
  experienceName?: string;
  city?: string;
  description?: string;
  characterName?: string;
  archetype?: string;
}

export interface ExperiencePoiGenerationInput {
  experienceId?: number;
  experienceName: string;
  city?: string;
  description?: string;
  latitude?: number | null;
  longitude?: number | null;
  prompt?: string;
  maxPois?: number;
}

export interface GeneratedExperiencePoi {
  id: string;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  mapsUrl: string;
  summary: string;
  suggestedQueries: string[];
}

export interface ExperiencePoiGenerationResult {
  pois: GeneratedExperiencePoi[];
  grounding: {
    model: string;
    apiVersion: string;
    groundingMode: string;
    webQueries: number;
    hasMapsToken: boolean;
    hasGroundingChunks: boolean;
    elapsedMs: number;
    modeTrace: Array<{
      groundingMode: string;
      status: number;
      elapsedMs: number;
      placesCount: number;
      webQueries: number;
      hasMapsToken: boolean;
      hasGroundingChunks: boolean;
      score: number;
      detail: string;
    }>;
  } | null;
}

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function buildFallbackExperience(location: string): GeminiExperienceData {
  const clean = location.trim();
  const city = clean.split(',')[0]?.trim() || clean || 'Territorio';
  return {
    name: `Scopri ${city}`,
    city,
    description:
      `Un percorso culturale dedicato a ${city}, con tappe pensate per raccontare in modo chiaro storia, identita e tradizioni locali.\n\n` +
      'L esperienza e progettata per offrire contenuti accessibili, stimolanti e progressivi, con attenzione alla fruizione mobile e alla personalizzazione narrativa.',
    nomeChatbot: `Il Custode di ${city}`,
    messIntermedio:
      `Stai esplorando ${city}. Posso guidarti tra storia, curiosita e dettagli utili, adattando il ritmo della narrazione ai tuoi interessi.`,
    messFinale:
      `Grazie per aver visitato ${city} con me. Quando vuoi, possiamo continuare con una nuova esperienza del territorio.`,
  };
}

function buildFallbackGeniusLoci(experienceName: string, location: string, archetype?: string): GeminiGeniusLociData {
  const city = location.trim().split(',')[0]?.trim() || location.trim() || 'questo luogo';
  const role = archetype?.trim() || 'Custode';
  return {
    name: `${role} di ${city}`,
    bio:
      `Il ${role.toLowerCase()} accompagna i visitatori nell esperienza "${experienceName}", intrecciando memoria storica e racconto contemporaneo. ` +
      `Il suo compito e valorizzare i dettagli del luogo, rendendo ogni tappa comprensibile, coinvolgente e coerente con il contesto culturale di ${city}.`,
    specialties: ['storia locale', 'curiosita del territorio', 'orientamento visita'],
    quote: 'Ogni luogo parla a chi sa ascoltare.',
    personality: archetype?.trim() || 'neutro, empatico, chiaro',
  };
}

async function callGemini(prompt: string): Promise<string> {
  void prompt;
  return '';
}

export async function generateExperienceFromLocation(location: string): Promise<GeminiExperienceData> {
  const prompt = `Sei un esperto di turismo culturale italiano. 
Genera i contenuti per una esperienza turistica Genius Loci per il luogo: "${location}".

Rispondi SOLO con un JSON valido (nessun markdown, nessun testo aggiuntivo) con questa struttura:
{
  "name": "Nome breve e evocativo dell'esperienza",
  "city": "Nome della città o comune",
  "description": "Descrizione dettagliata dell'esperienza (2-3 paragrafi), storia, caratteristiche, cosa scoprirà il visitatore",
  "nomeChatbot": "Nome del Genius Loci (personaggio storico o mitico legato al luogo)",
  "messIntermedio": "Messaggio guida durante l'esperienza (50-80 parole), in prima persona come il Genius Loci",
  "messFinale": "Messaggio di congedo del Genius Loci (30-50 parole), in prima persona"
}`;

  const raw = await callGemini(prompt);
  const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  try {
    return JSON.parse(cleaned) as GeminiExperienceData;
  } catch {
    return buildFallbackExperience(location);
  }
}

export async function generateGeniusLociPersonality(
  experienceName: string,
  location: string,
  archetype?: string,
): Promise<GeminiGeniusLociData> {
  const prompt = `Sei un esperto di storytelling e turismo culturale.
Crea un personaggio Genius Loci (spirito del luogo) per l'esperienza "${experienceName}" a "${location}".
${archetype ? `Archetipo Jung selezionato: ${archetype}.` : ''}

Rispondi SOLO con un JSON valido con questa struttura:
{
  "name": "Nome del personaggio",
  "bio": "Biografia del personaggio (100-150 parole), chi è, la sua storia, il suo legame con il luogo",
  "specialties": ["specialità1", "specialità2", "specialità3"],
  "quote": "Una citazione iconica del personaggio (max 20 parole)",
  "personality": "Descrizione della personalità in 2-3 aggettivi"
}`;

  const raw = await callGemini(prompt);
  const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
  try {
    return JSON.parse(cleaned) as GeminiGeniusLociData;
  } catch {
    return buildFallbackGeniusLoci(experienceName, location, archetype);
  }
}

/** Compilazione guidata da Gemini: usa la Cloud Function come sorgente primaria per mantenere APE allineato. */
export async function autofillExperienceFields(
  location: string,
  options: ExperienceAutofillOptions = {},
): Promise<{
  experience: GeminiExperienceData;
  genius: GeminiGeniusLociData;
}> {
  const trimmed = String(location || '').trim();
  if (!trimmed) {
    throw new Error('location obbligatorio');
  }

  try {
    const response = await fetch('/api/generate-experience-autofill', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        location: trimmed,
        experienceName: String(options.experienceName || '').trim(),
        city: String(options.city || '').trim(),
        description: String(options.description || '').trim(),
        characterName: String(options.characterName || '').trim(),
        archetype: String(options.archetype || '').trim(),
      }),
    });

    const raw = await response.text();
    let parsed: {
      success?: boolean;
      error?: string;
      experience?: GeminiExperienceData;
      genius?: GeminiGeniusLociData;
    } | null = null;
    try {
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      parsed = null;
    }

    if (!response.ok || !parsed?.success || !parsed.experience || !parsed.genius) {
      const detail = parsed?.error || raw || `Errore ${response.status}`;
      throw new Error(detail);
    }

    return { experience: parsed.experience, genius: parsed.genius };
  } catch (apiError) {
    throw apiError;
  }
}

export async function generateExperiencePois(
  payload: ExperiencePoiGenerationInput,
): Promise<ExperiencePoiGenerationResult> {
  const maxAttempts = 2;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetch('/api/generate-experience-pois', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const raw = await response.text();
      let parsed: {
        success?: boolean;
        error?: string;
        pois?: GeneratedExperiencePoi[];
        grounding?: ExperiencePoiGenerationResult['grounding'];
      } | null = null;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.success) {
        return {
          pois: Array.isArray(parsed.pois) ? parsed.pois : [],
          grounding: parsed.grounding || null,
        };
      }

      const detail = parsed?.error || raw || `Errore ${response.status}`;
      const retryableStatus = response.status === 429 || response.status === 502 || response.status === 503 || response.status === 504;
      if (retryableStatus && attempt < maxAttempts) {
        // Upstream Cloud Run/Gateway può restituire errori transitori sotto carico.
        // Retry singolo per evitare falso negativo in UI.
        // eslint-disable-next-line no-await-in-loop
        await delayMs(1200);
        continue;
      }

      throw new Error(detail);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error || 'Errore generazione POI'));
      if (attempt < maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await delayMs(1200);
        continue;
      }
      break;
    }
  }

  throw lastError || new Error('Errore generazione POI');
}
